import {
    faCheckCircle,
    faExclamationTriangle,
    faHourglassStart,
    faSpinner
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Alert, Button, Col, Container, Form, Row } from "react-bootstrap";
import Table from 'react-bootstrap/Table';
import Markdown from "react-markdown";
import { callQuery, LLM_VISIT_FEEDBACK_BY_CSN, LLMVisitFeedback, PATIENT_VISIT_BY_CSN } from "../../../amplify/api-gateway/query";
import { PatientVisit } from "../../../amplify/api-gateway/search-patients";
import { callWrite, UPDATE_LLM_VISIT_FEEDBACK } from "../../../amplify/api-gateway/write";
import { readBinaryFile } from "../../../amplify/s3/storage";
import useSetPagePatientVisit from "../../common/GlobalOptionsContext";
import CustomSurveyForm from "./CustomSurveyForm";

interface DashboardProps {
    csn: string;
    tenant?: string;
}

export const COHORT_ADMIT_TEST_DASHBOARD_PATH = 'cohort_admit_test/2025-06-24';
export const FEEDBACK_TYPE_SUMMARY = "cohort_admit_summary_ed";
export const FEEDBACK_TYPE_DIAGNOSTIC_UNCERTAINTY = "cohort_admit_diagnostic_uncertainty_ed";
export const FEEDBACK_TYPE_DIAGNOSTIC_UNCERTAINTY_JUSTIFICATION = "cohort_admit_diagnostic_uncertainty_justification";
export const FEEDBACK_TYPE_APPROPRIATE_LOC_PROSPECTIVE = "cohort_admit_appropriate_loc_prospective";
export const FEEDBACK_TYPE_APPROPRIATE_LOC_PROSPECTIVE_JUSTIFICATION = "cohort_admit_appropriate_loc_prospective_justification";
export const FEEDBACK_TYPE_APPROPRIATE_LOC_RETROSPECTIVE = "cohort_admit_appropriate_loc_retrospective";
export const FEEDBACK_TYPE_APPROPRIATE_LOC_RETROSPECTIVE_JUSTIFICATION = "cohort_admit_appropriate_loc_retrospective_justification";
export const FEEDBACK_TYPE_SIGNIFICANT_FINDINGS = "cohort_admit_significant_findings";
export const FEEDBACK_TYPE_SIGNIFICANT_FINDINGS_JUSTIFICATION = "cohort_admit_significant_findings_justification";
export const FEEDBACK_TYPE_DIAGNOSTIC_CLARITY = "cohort_admit_diagnostic_clarity";
export const FEEDBACK_TYPE_DIAGNOSTIC_CLARITY_JUSTIFICATION = "cohort_admit_diagnostic_clarity_justification";
export const FEEDBACK_TYPE_MODIFIABLE_ADMISSION = "cohort_admit_modifiable_admission";
export const FEEDBACK_TYPE_MODIFIABLE_ADMISSION_JUSTIFICATION = "cohort_admit_modifiable_admission_justification";
export const FEEDBACK_TYPE_CDS_OPPORTUNITY = "cohort_admit_cds_opportunity";
export const FEEDBACK_TYPE_CDS_OPPORTUNITY_DETAIL = "cohort_admit_cds_opportunity_detail";
export const FEEDBACK_TYPE_STREAMLINE_ADMISSION = "cohort_admit_streamline_admission";
export const FEEDBACK_TYPE_STREAMLINE_ADMISSION_JUSTIFICATION = "cohort_admit_streamline_admission_justification";

export const CohortAdmitDashboard = ({
    csn,
    tenant = 'stanford'
}: DashboardProps) => {

    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<any>();
    const [feedbackTargetToSurvey, setFeedbackTargetToSurvey] = useState<any>({});
    const [errorMessage, setErrorMessage] = useState("");

    // Add patientVisit state directly in the component
    const [patientVisit, setPatientVisit] = useState<PatientVisit>();
    useSetPagePatientVisit(patientVisit);

    // Add function to fetch patient visit data
    const fetchPatientVisit = async () => {
        try {
            const patients = await callQuery({
                operation: PATIENT_VISIT_BY_CSN,
                csn: csn,
            });
            
            if (Array.isArray(patients) && patients.length > 0) {
                setPatientVisit(patients[0]);
            } else {
                setPatientVisit(undefined);
            }
        } catch (error) {
            console.error("Error fetching patient visit:", error);
            setPatientVisit(undefined);
        }
    };

    // Function to fetch dashboard data
    const fetchDashboardData = async () => {
        setIsLoading(true);
        const decoder = new TextDecoder();
        
        try {
            // Fetch the actual data
            const dashboardPath = `${COHORT_ADMIT_TEST_DASHBOARD_PATH}/${csn}.json`;
            const data = await readBinaryFile(
                dashboardPath,
                tenant,
                'ed-llm'
            );

            const jsonData = JSON.parse(decoder.decode(data));
            
            // Set all required state variables
            setData(jsonData || {});
            
            // Fetch survey data
            await fetchSurveyData();
            
        } catch (error) {
            console.error("Error fetching data:", error);
            setErrorMessage("Could not load data. Check that you are on the Stanford network and if using VPN that your VPN Group is set to either 'CardinalKey-Full' or 'Stanford-Full'");
        } finally {
            setIsLoading(false);
        }
    };
    
    // Function to fetch survey data
    const fetchSurveyData = async () => {
        try {
            console.log("Fetching survey data for CSN:", csn);
            const feedbackItems = await callQuery({
                operation: LLM_VISIT_FEEDBACK_BY_CSN,
                csn,
            }) as LLMVisitFeedback[];
            
            console.log("Survey data result:", feedbackItems);
            
            if (feedbackItems && feedbackItems.length > 0) {
                // Get all original survey feedback items
                const originalSurveyFeedbackItems = feedbackItems.filter(
                    (item: LLMVisitFeedback) => 
                        item.feedback_type === 'survey' && 
                        (item.feedback_target.startsWith("cohort_admit_"))
                );

                const feedbackTargetToSurvey: any = {};

                for (let item of originalSurveyFeedbackItems) {
                    const survey = JSON.parse(item.note);
                    feedbackTargetToSurvey[item.feedback_target] = {
                        "option": survey.option,
                        "comment": survey.comment,
                    };
                }

                setFeedbackTargetToSurvey(feedbackTargetToSurvey);
            }
        } catch (error) {
            console.error("Error fetching survey data:", error);
        }
    };

    const handleSave = async (feedback_target: string, data: any): Promise<boolean> => {
        try {
            const response = await callWrite({
                operation: UPDATE_LLM_VISIT_FEEDBACK,
                creation_time: new Date().getTime(),
                csn: csn,
                mrn: '',
                feedback_target: feedback_target,
                feedback_type: 'survey',
                note: JSON.stringify(data),
            });
            
            // Check if response is empty or has an error
            if (!response || Object.keys(response).length === 0) {
                window.alert("Failed to submit survey. Please refresh and try again.");
                return false;
            }

            // Refresh the survey data after submission
            await fetchSurveyData();
            
            return true;
        } catch (error) {
            console.error("Error submitting original survey:", error);
            
            // Show alert with error details
            if (error instanceof Error) {
                window.alert(`Failed to submit survey: ${error.message}`);
            } else {
                window.alert("Failed to submit survey. An unknown error occurred.");
            }

            return false;
        }
    };

    const getIsCompleted = () => {
        const isCompleted = feedbackTargetToSurvey[COHORT_ADMIT_TEST_DASHBOARD_PATH] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_SUMMARY] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_DIAGNOSTIC_UNCERTAINTY] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_DIAGNOSTIC_UNCERTAINTY_JUSTIFICATION] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_APPROPRIATE_LOC_PROSPECTIVE] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_APPROPRIATE_LOC_PROSPECTIVE_JUSTIFICATION] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_APPROPRIATE_LOC_RETROSPECTIVE] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_APPROPRIATE_LOC_RETROSPECTIVE_JUSTIFICATION] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_SIGNIFICANT_FINDINGS] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_SIGNIFICANT_FINDINGS_JUSTIFICATION] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_DIAGNOSTIC_CLARITY] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_DIAGNOSTIC_CLARITY_JUSTIFICATION] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_MODIFIABLE_ADMISSION] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_MODIFIABLE_ADMISSION_JUSTIFICATION] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_CDS_OPPORTUNITY] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_CDS_OPPORTUNITY_DETAIL] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_STREAMLINE_ADMISSION] &&
            feedbackTargetToSurvey[FEEDBACK_TYPE_STREAMLINE_ADMISSION_JUSTIFICATION];


        return (
            <Row>
                <Col>
                    {
                        isCompleted ? (
                            <Alert variant="success">
                                <span className="me-1"><FontAwesomeIcon icon={faCheckCircle} /></span> Survey Completed
                            </Alert>
                        ) : (
                            <Alert variant="info">
                                <span className="me-1"><FontAwesomeIcon icon={faHourglassStart} /></span> Survey Not Yet Completed
                            </Alert>
                        )
                    }
                </Col>
            </Row>
        );
    };

    const getSectionHeader = (title: string) => {
        return (
            <Row>
                <Col>
                    <div className="survey-section p-3 mb-4">
                        {title}
                    </div>
                </Col>
            </Row>
        );
    };

    const getContextSection = (title: string, data: string | string[], horizontalLineAbove: boolean = true) => {
        return (
            <>
                {horizontalLineAbove && <hr />}
                <Row className="mb-2">
                    <Col>
                        <strong>{title}</strong>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {Array.isArray(data) ? (
                            <div className="survey-context">
                                <ul>
                                    {data.map((item, index) => (
                                        <li key={index}>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="survey-context">
                                {data}
                            </div>
                        )}
                    </Col>
                </Row>
            </>
        );
    }

    const getSectionToRate = (title: string, data: string, surveyTitle: string, 
        surveyDescription: string, surveyOptions: string[], surveyKey: string) => {
        return (
            <>
                <hr />
                <Row>
                    <Col>
                        <div>
                            <strong>{title}</strong>
                        </div>
                        <div className="survey-context">
                            {data}
                        </div>
                    </Col>
                    <Col>
                        <CustomSurveyForm
                            title={surveyTitle}
                            altTitle={title}
                            description={surveyDescription}
                            options={surveyOptions}
                            onSubmit={(data) => handleSave(surveyKey, data)}
                            enableComment={true}
                            initialSelectedOption={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].option : undefined}
                            initialComment={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].comment : undefined}
                        />
                    </Col>
                </Row>
            </>
        )
    }

    // Update useEffect to also fetch patient visit data
    useEffect(() => {
        if (csn) {
            fetchDashboardData();
            fetchPatientVisit();
        }
    }, [csn, tenant]);

    if (isLoading) {
        return (
            <Row style={{textAlign: 'center'}}>
                <Col md={12}>
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading dashboard data...
                </Col>
            </Row>
        );
    }
    
    if (errorMessage) {
        return (
            <Row style={{textAlign: 'center'}}>
                <Col md={12}>
                    <Alert variant="danger">
                        <FontAwesomeIcon icon={faExclamationTriangle} /> {errorMessage}
                    </Alert>
                </Col>
            </Row>
        );
    }

    if (!data) {
        return <></>
    }

    return (
        <Container>
            <Row className="mb-3">
                <Col>
                    <div>
                        <Row>
                            <Col>
                                <h5>Cohort Admit Rating</h5>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <small>
                                    <strong>CSN</strong> {csn} &bull; <strong>MRN</strong> {data.MRN}
                                </small>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
            {getIsCompleted()}
            {getContextSection("Provider Note", data.Note_ED_Provider, false)}
            {getContextSection("Hospital Discharge Summary", data.Note_Discharge_Summary)}
            {getSectionToRate("ED Summary", data.summary_ed, "Accuracy", 
                "Please rate the accuracy of this generated summary.", 
                ['Accurate', 'Partially accurate', 'Inaccurate'], FEEDBACK_TYPE_SUMMARY)}

            {getContextSection("ED Diagnoses", data.diagnoses_ed, false)}
            {getSectionToRate("ED Diagnostic Uncertainty", data.diagnostic_uncertainty_ed, "Uncertainty", 
                "MD assessment of ED diagnostic uncertainty (select one)", 
                ['Low', 'Moderate', 'High'], FEEDBACK_TYPE_DIAGNOSTIC_UNCERTAINTY)}

            {getSectionToRate(
                "ED Diagnostic Uncertainty Reasoning", 
                data.diagnostic_uncertainty_justification, 
                "Uncertainty Reasoning", 
                "Reasoning quality:", 
                ['Good', 'Incomplete', 'Poor'], 
                FEEDBACK_TYPE_DIAGNOSTIC_UNCERTAINTY_JUSTIFICATION
            )}

            {getSectionToRate(
                "Appropriate Disposition and Level of Care (Prospective)", 
                data.appropriate_loc_prospective, 
                "Prospective Appropriateness", 
                "Based only on information available at the time of admission, was the disposition (inpatient/observation) and level of care (acute/IICU/critical) medically appropriate? Choose one.", 
                ['Appropriate', 'Should be higher level of care', 'Should be lower level of care', 'Should be outpatient', 'Should be outpatient with post-discharge monitoring'], 
                FEEDBACK_TYPE_APPROPRIATE_LOC_PROSPECTIVE
            )}

            {getSectionToRate(
                "Prospective Reasoning", 
                data.appropriate_loc_prospective_justification, 
                "Prospective Reasoning", 
                "Reasoning quality:", 
                ['Good', 'Incomplete', 'Poor'], 
                FEEDBACK_TYPE_APPROPRIATE_LOC_PROSPECTIVE_JUSTIFICATION
            )}

            {getSectionToRate(
                "Appropriate ED Disposition and Level of Care (Retrospective)", 
                data.appropriate_loc_retrospective, 
                "Retrospective Appropriateness", 
                "Considering the entire hospital course, was the disposition (inpatient/observation) and level of care (acute/IICU/critical) medically appropriate? Choose one.", 
                ['Appropriate', 'Should be higher level of care', 'Should be lower level of care', 'Should be outpatient', 'Should be outpatient with post-discharge monitoring'], 
                FEEDBACK_TYPE_APPROPRIATE_LOC_RETROSPECTIVE
            )}

            {getSectionToRate(
                "Retrospective Reasoning", 
                data.appropriate_loc_retrospective_justification, 
                "Retrospective Reasoning", 
                "Reasoning quality:", 
                ['Good', 'Incomplete', 'Poor'], 
                FEEDBACK_TYPE_APPROPRIATE_LOC_RETROSPECTIVE_JUSTIFICATION
            )}

            {getSectionToRate(
                "Significant Findings", 
                data.significant_findings, 
                "Significant Findings", 
                "Were there critical diagnoses, complications, deteriorations, or findings made during the hospitalization that were not apparent or strongly suspected at the time of ED evaluation, and that significantly altered the patient's management or prognosis?", 
                ['Yes', 'Possibly', 'No'], 
                FEEDBACK_TYPE_SIGNIFICANT_FINDINGS
            )}

            {getSectionToRate(
                "Significant Findings Reasoning", 
                data.significant_findings_justification, 
                "Significant Findings Reasoning", 
                "Reasoning quality:", 
                ['Good', 'Incomplete', 'Poor'], 
                FEEDBACK_TYPE_SIGNIFICANT_FINDINGS_JUSTIFICATION
            )}

            {getSectionToRate(
                "Diagnostic Clarity", 
                data.diagnostic_clarity, 
                "Diagnostic Clarity", 
                "How much did the diagnostic picture evolve from presentation to discharge?", 
                ['ED diagnosis confirmed', 'ED diagnosis refined', 'ED diagnosis changed'], 
                FEEDBACK_TYPE_DIAGNOSTIC_CLARITY
            )}

            {getSectionToRate(
                "Diagnostic Clarity Reasoning", 
                data.diagnostic_clarity_justification, 
                "Diagnostic Clarity Reasoning", 
                "Reasoning quality:", 
                ['Good', 'Incomplete', 'Poor'], 
                FEEDBACK_TYPE_DIAGNOSTIC_CLARITY_JUSTIFICATION
            )}

            {getSectionToRate(
                "Modifiable Admission", 
                data.modifiable_admission, 
                "Modifiable Admission", 
                "Based on the entire clinical picture, could specific actions or processes, if done differently in the ED or a robust outpatient setting, have prevented this admission or allowed for a lower/more resource-efficient level of care? Select one:", 
                ['Not modifiable', 'Modifiable with outpatient care', 'Modifiable with urgent (<24h) outpatient care', 'Modifiable with post-discharge monitoring', 'Modifiable with better ED workup/management', 'Modifiable by addressing social determinants of health', 'Other (explain)'], 
                FEEDBACK_TYPE_MODIFIABLE_ADMISSION
            )}

            {getSectionToRate(
                "Modifiability Reasoning", 
                data.modifiable_admission_justification, 
                "Modifiability Reasoning", 
                "Reasoning quality:", 
                ['Good', 'Incomplete', 'Poor'], 
                FEEDBACK_TYPE_MODIFIABLE_ADMISSION_JUSTIFICATION
            )}

            {getSectionToRate(
                "CDS Opportunity", 
                data.cds_opportunity, 
                "CDS Opportunity", 
                "Could the admission have been prevented/improved to a more suitable resource level with ED clinical decision support?", 
                ['Yes', 'No', 'Possibly'], 
                FEEDBACK_TYPE_CDS_OPPORTUNITY
            )}

            {getSectionToRate(
                "CDS Reasoning", 
                data.cds_opportunity_detail, 
                "CDS Reasoning", 
                "Reasoning quality:", 
                ['Good', 'Incomplete', 'Poor'], 
                FEEDBACK_TYPE_CDS_OPPORTUNITY_DETAIL
            )}

            {getSectionToRate(
                "Admission Streamlining Opportunities", 
                data.streamline_admission, 
                "Admission Streamlining", 
                "Did the inpatient stay involve any potentially avoidable delays or inefficiencies that prolonged the length of stay without clear medical benefit? Consider delays in testing, consultations, or treatments.", 
                ['No', 'Delayed testing', 'Delayed consultation', 'Delayed treatment', 'Other (please explain)'], 
                FEEDBACK_TYPE_STREAMLINE_ADMISSION
            )}

            {getSectionToRate(
                "Admission Efficiency Reasoning", 
                data.streamline_admission_justification, 
                "Admission Efficiency Reasoning", 
                "Reasoning quality:", 
                ['Good', 'Incomplete', 'Poor'], 
                FEEDBACK_TYPE_STREAMLINE_ADMISSION_JUSTIFICATION
            )}

        </Container>
    );
};
